/**
 * Column visibility, and creating custom columns.
 *
 * Custom columns are the curator's own vocabulary — "Cleared for air", "Station
 * rating", "Intro notes". They live in the database rather than in tags, because
 * they are Nova's opinion about a track, not the file's, and writing them into
 * every file would be both slow and presumptuous.
 *
 * Deleting one destroys the values under it, so that action confirms and is the
 * only destructive control here.
 */

import { useState } from "react";
import type { ColumnDef } from "./columns";

export interface NewColumn {
  label: string;
  valueType: "text" | "number" | "boolean" | "date";
}

export function ColumnPicker({
  columns,
  onToggle,
  onReset,
  onCreate,
  onDelete,
  onClose,
}: {
  columns: ColumnDef[];
  onToggle: (id: string) => void;
  onReset: () => void;
  onCreate: (col: NewColumn) => Promise<string | null>;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  const [label, setLabel] = useState("");
  const [valueType, setValueType] = useState<NewColumn["valueType"]>("text");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    const name = label.trim();
    if (!name) return;
    setBusy(true);
    setError(await onCreate({ label: name, valueType }));
    setBusy(false);
    setLabel("");
  };

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="popover column-picker" role="dialog" aria-label="Columns">
        <div className="popover-head">
          <span>Columns</span>
          <button className="icon-button" onClick={onClose} title="Close">×</button>
        </div>

        <div className="popover-body">
          <div className="picker-list">
            {columns.map((col) => (
              <label
                key={col.id}
                className={`picker-row ${col.locked ? "locked" : ""}`}
                title={col.locked ? "Always shown" : undefined}
              >
                <input
                  type="checkbox"
                  checked={!col.hidden}
                  disabled={col.locked}
                  onChange={() => onToggle(col.id)}
                />
                <span className="picker-label">{col.label}</span>
                {col.kind === "custom" && (
                  <>
                    <span className="picker-badge">{col.valueType}</span>
                    <button
                      className="picker-delete"
                      title={`Delete "${col.label}" and every value stored in it`}
                      onClick={(e) => {
                        e.preventDefault();
                        if (
                          confirm(
                            `Delete the column "${col.label}"?\n\n` +
                              "Every value curators have stored in it will be lost. " +
                              "This cannot be undone."
                          )
                        ) {
                          onDelete(col.id);
                        }
                      }}
                    >
                      ×
                    </button>
                  </>
                )}
              </label>
            ))}
          </div>

          <div className="popover-section">New column</div>
          <div className="new-column">
            <input
              value={label}
              placeholder="Column name…"
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void create()}
              spellCheck={false}
            />
            <select
              value={valueType}
              onChange={(e) => setValueType(e.target.value as NewColumn["valueType"])}
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="boolean">Yes / no</option>
              <option value="date">Date</option>
            </select>
            <button
              className="primary"
              onClick={() => void create()}
              disabled={busy || !label.trim()}
            >
              {busy ? "…" : "Add"}
            </button>
          </div>
          {error && <div className="picker-error">{error}</div>}
          <p className="note">
            Custom values are stored in Nova's database, keyed by audio content —
            so they survive a file being moved, renamed or re-tagged.
          </p>
        </div>

        <div className="popover-foot">
          <button className="ghost" onClick={onReset}>Reset layout</button>
        </div>
      </div>
    </>
  );
}
